import DOMPurify from "dompurify";
import { marked } from "marked";
import type { TocItem } from "./types";

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s/]+/g, "-")
    .replace(/[^\p{L}\p{N}-]/gu, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "section";
}

function uniqueId(base: string, used: Set<string>): string {
  let id = base;
  let index = 2;
  while (used.has(id)) {
    id = `${base}-${index}`;
    index += 1;
  }
  used.add(id);
  return id;
}

export function renderMarkdown(markdown: string): string {
  marked.setOptions({ gfm: true, breaks: false });
  const raw = marked.parse(markdown, { async: false }) as string;
  return DOMPurify.sanitize(raw, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form", "input", "button"],
    FORBID_ATTR: ["style", "onerror", "onclick", "onload"],
  });
}

export function enhanceReader(reader: HTMLElement): TocItem[] {
  const usedIds = new Set<string>();
  const toc: TocItem[] = [];

  reader.querySelectorAll<HTMLHeadingElement>("h1, h2, h3").forEach((heading) => {
    const id = uniqueId(slugify(heading.textContent || "section"), usedIds);
    heading.id = id;
    if (heading.tagName === "H2" || heading.tagName === "H3") {
      toc.push({
        id,
        level: heading.tagName === "H2" ? 2 : 3,
        title: (heading.textContent || "未命名章节").trim(),
      });
    }
  });

  reader.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((anchor) => {
    try {
      const url = new URL(anchor.getAttribute("href") || "", window.location.href);
      if (url.origin !== window.location.origin) {
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
      }
    } catch {
      anchor.removeAttribute("href");
    }
  });

  reader.querySelectorAll<HTMLImageElement>("img").forEach((image) => {
    image.loading = "lazy";
    image.decoding = "async";
  });

  reader.querySelectorAll<HTMLTableElement>("table").forEach((table) => {
    if (table.parentElement?.classList.contains("table-scroll")) return;
    const parent = table.parentNode;
    if (!parent) return;
    const shell = document.createElement("div");
    shell.className = "table-scroll-shell";
    const wrapper = document.createElement("div");
    wrapper.className = "table-scroll";
    wrapper.tabIndex = 0;
    wrapper.setAttribute("role", "region");
    wrapper.setAttribute("aria-label", "可横向滚动的表格");
    parent.insertBefore(shell, table);
    shell.appendChild(wrapper);
    wrapper.appendChild(table);

    const updateScrollCue = (): void => {
      const hasOverflow = wrapper.scrollWidth > wrapper.clientWidth + 1;
      const atEnd = !hasOverflow || wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 1;
      shell.classList.toggle("has-overflow", hasOverflow);
      shell.classList.toggle("at-end", atEnd);
    };
    wrapper.addEventListener("scroll", updateScrollCue, { passive: true });
    if ("ResizeObserver" in window) {
      new ResizeObserver(updateScrollCue).observe(wrapper);
    }
    updateScrollCue();
  });

  const selector = "h1, h2, h3, p, li, blockquote, td";
  const blocks = Array.from(reader.querySelectorAll<HTMLElement>(selector)).filter((block) => {
    if (!(block.textContent || "").trim()) return false;
    const parentBlock = block.parentElement?.closest<HTMLElement>(selector);
    return !parentBlock || !reader.contains(parentBlock);
  });
  blocks.forEach((block, index) => {
    block.dataset.reviewBlock = String(index);
  });

  return toc;
}
